import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Link,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
  Paper,
  Container,
  IconButton,
  FormControlLabel,
  Stack,
} from '@mui/material';
import CookieIcon from '@mui/icons-material/Cookie';
import CloseIcon from '@mui/icons-material/Close';

// Transition component for the banner slide-up animation
const BannerTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function CookieConsent() {
  // State to manage the visibility of the banner and the settings dialog
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // State for cookie preferences. Essential cookies are always enabled.
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  // Check for consent on component mount
  useEffect(() => {
    const consent = localStorage.getItem('user_cookie_consent');
    if (!consent) {
      // Use a timeout to avoid layout shift issues on initial load
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      // If consent is already given, parse it and update the state
      try {
        const parsedConsent = JSON.parse(consent);
        setCookiePreferences(parsedConsent);
      } catch (error) {
        console.error("Failed to parse cookie consent from localStorage", error);
        setShowBanner(true);
      }
    }
  }, []);

  /**
   * Saves the user's consent to localStorage and hides the banner/dialog.
   * @param {object} preferences - The cookie preferences to save.
   */
  const saveConsent = (preferences) => {
    localStorage.setItem('user_cookie_consent', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
    // Here you would typically initialize services based on consent
    // e.g., if (preferences.analytics) { initAnalytics(); }
    // You might need to reload the page to apply changes
    // window.location.reload();
  };

  // Handler for "Accept All"
  const handleAcceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true };
    setCookiePreferences(allAccepted);
    saveConsent(allAccepted);
  };

  // Handler for "Reject All" (except essential)
  const handleRejectAll = () => {
    const essentialOnly = { essential: true, analytics: false, marketing: false };
    setCookiePreferences(essentialOnly);
    saveConsent(essentialOnly);
  };

  // Handler for saving custom preferences from the dialog
  const handleSavePreferences = () => {
    saveConsent(cookiePreferences);
  };

  // Toggles the state of a specific cookie category
  const handlePreferenceChange = (event) => {
    const { name, checked } = event.target;
    setCookiePreferences(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Render nothing if the banner should not be shown
  if (!showBanner) return null;

  return (
    <>
      {/* Main Cookie Banner */}
      <Box
        component={Paper}
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.modal + 1, // Ensure it's above other content
          borderTop: '1px solid',
          borderColor: 'divider',
          display: showBanner ? 'block' : 'none',
        }}
      >
        <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
            <Container maxWidth="lg" sx={{ p: 2 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={{ xs: 2, sm: 3 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CookieIcon sx={{ mr: 2, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            We use cookies to improve your experience and analyse site traffic. For more information, please see our{' '}
                            <Link href="/privacy" underline="hover">Privacy Policy</Link>.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
                        <Button variant="outlined" size="small" onClick={() => setShowSettings(true)}>
                            Manage cookies
                        </Button>
                        <Button variant="contained" size="small" onClick={handleAcceptAll}>
                            Accept all
                        </Button>
                    </Stack>
                </Stack>
            </Container>
        </Slide>
      </Box>


      {/* Cookie Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Cookie Preferences
          <IconButton
            aria-label="close"
            onClick={() => setShowSettings(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
            <Stack spacing={2}>
                <Box>
                    <Typography gutterBottom variant="h6" component="div">Essential Cookies</Typography>
                    <DialogContentText variant="body2" color="text.secondary">
                        These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you, such as setting your privacy preferences.
                    </DialogContentText>
                </Box>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={cookiePreferences.analytics}
                                onChange={handlePreferenceChange}
                                name="analytics"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="subtitle1" component="div">Analytics Cookies</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">Allows us to count visits and traffic sources to improve site performance.</Typography>
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start', ml: 0 }}
                    />
                </Paper>
                 <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={cookiePreferences.marketing}
                                onChange={handlePreferenceChange}
                                name="marketing"
                            />
                        }
                        label={
                             <Box>
                                <Typography variant="subtitle1" component="div">Marketing Cookies</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">Used by advertising partners to show you relevant ads on other sites.</Typography>
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start', ml: 0 }}
                    />
                </Paper>
            </Stack>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleRejectAll} sx={{ mr: 'auto' }}>Reject All</Button>
          <Button onClick={handleSavePreferences} variant="contained">Accept Selected</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
